import React, { useEffect, useState } from 'react';

interface RemoteCursor {
  userId: string;
  email: string;
  color: string;
  x: number; // canvas coordinates
  y: number;
  lastSeen: number;
}

interface PresenceLayerProps {
  // Subscribes `listener` to cursor-map updates (calling it once immediately
  // with the current map) and returns an unsubscribe function. A plain object
  // prop was replaced with this subscription so a cursor broadcast — which can
  // fire ~30x/s per other active user — only re-renders this component, not
  // whichever large component happens to own the presence hook.
  subscribeCursors: (listener: (cursors: Record<string, RemoteCursor>) => void) => () => void;
}

// Render cursors directly in canvas space (inside the transformed div)
export default function PresenceLayer({ subscribeCursors }: PresenceLayerProps) {
  const [cursors, setCursors] = useState<Record<string, RemoteCursor>>({});
  useEffect(() => subscribeCursors(setCursors), [subscribeCursors]);

  const [, forceUpdate] = useState(0);

  // Re-render every 2s to fade out stale cursors
  useEffect(() => {
    const interval = setInterval(() => forceUpdate(n => n + 1), 2000);
    return () => clearInterval(interval);
  }, []);

  const now = Date.now();

  return (
    <>
      {Object.values(cursors).map((cursor) => {
        const age = now - cursor.lastSeen;
        if (age > 5000) return null; // hide after 5s of inactivity
        const opacity = age > 3000 ? 1 - (age - 3000) / 2000 : 1;

        return (
          <div
            key={cursor.userId}
            style={{
              position: 'absolute',
              left: cursor.x,
              top: cursor.y,
              pointerEvents: 'none',
              zIndex: 9000,
              opacity,
              transform: 'translate(-2px, -2px)',
              transition: 'left 0.08s linear, top 0.08s linear',
            }}
          >
            {/* Cursor SVG */}
            <svg width="20" height="22" viewBox="0 0 20 22" fill="none">
              <path
                d="M4 2L16 10L10 11.5L7.5 18L4 2Z"
                fill={cursor.color}
                stroke="white"
                strokeWidth="1.5"
                strokeLinejoin="round"
              />
            </svg>
            {/* Name label */}
            <div
              style={{
                position: 'absolute',
                top: 18,
                left: 12,
                background: cursor.color,
                color: 'white',
                fontSize: 11,
                fontWeight: 600,
                padding: '2px 6px',
                borderRadius: 4,
                whiteSpace: 'nowrap',
                boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
              }}
            >
              {cursor.email.split('@')[0]}
            </div>
          </div>
        );
      })}
    </>
  );
}
